import * as React from 'react';
import styled from 'styled-components';
import { Button, Tag, theme } from 'components';
import { AppointmentController, ContactController } from 'api';
import { BuildJsonType, EnhancedAppointment } from '@bb/domain/dist/types';
import errorReporter from '@app/errorReporter';
import { formatTimeStr, formatDateStrWithYearAndDay } from 'utils';
import { IRootScopeService } from 'angular';

import { formatStatus } from '../../helpers';

import { InviteBuyer } from '../../../Router/components';

const StyledTable = styled.table`
  width: 100%;
  .table-key,
  .appointment-status,
  label,
  strong {
    text-transform: uppercase;
  }
`;

type AppointmentDetailSectionProps = {
  appointment: BuildJsonType<EnhancedAppointment>;
  calendarSyncButton?: React.ReactNode;
  contactType?: string;
  external?: boolean;
  $rootScope?: IRootScopeService;
};

const AppointmentDetailSection: React.FunctionComponent<
  AppointmentDetailSectionProps
> = ({
  appointment,
  calendarSyncButton,
  contactType,
  external,
  $rootScope,
}) => (
    <>
      <label>Showing Details</label>
      <StyledTable className="table">
        <tbody>
          <Address appointment={appointment} />
          {(contactType === 'seller' ||
            appointment.status !== 'suggested_time_change') && (
            <AptDate
              appointment={appointment}
              calendarSyncButton={calendarSyncButton}
            />
          )}
          {contactType === 'seller' ? (
            <>
              <Agent appointment={appointment} />
              <Status appointment={appointment} />
            </>
          ) : (
            <>
              {appointment.status === 'suggested_time_change' ? (
                <>
                  <OriginalApt appointment={appointment} />
                  <NewSuggestedApt appointment={appointment} />
                </>
              ) : (
                <>
                  {['landlord/manager', 'tenant'].includes(contactType) && (
                    <Agent appointment={appointment} />
                  )}
                  <Status appointment={appointment} />
                </>
              )}
            </>
          )}
          {!external && (
            <Buyers appointment={appointment} $rootScope={$rootScope} />
          )}
        </tbody>
      </StyledTable>
    </>
  );

type RowProps = {
  appointment: BuildJsonType<EnhancedAppointment>;
  calendarSyncButton?: React.ReactNode;
  $rootScope?: IRootScopeService;
};

const Address: React.FC<RowProps> = ({ appointment }) => (
    <tr>
      <td className="table-key">Address</td>
      <td style={{ textTransform: 'uppercase' }}>
        {appointment.listing.line1} {appointment.listing.line2}
      </td>
    </tr>
  );

const AptDate: React.FC<RowProps> = ({ appointment, calendarSyncButton }) => {
  const { timezone } = appointment.listing;
  const dateStrWithYear = formatDateStrWithYearAndDay(
    appointment.time,
    timezone,
  );
  const timeStr = formatTimeStr(appointment.time, timezone);
  const timeEndStr = formatTimeStr(appointment.timeEnd, timezone);

  return (
    <tr>
      <td className="table-key">Date</td>
      <td>
        <div>
          <label>{dateStrWithYear}</label>
        </div>
        <div>{`${timeStr} - ${timeEndStr}`}</div>
        {calendarSyncButton}
      </td>
    </tr>
  );
};

const Agent: React.FC<RowProps> = ({ appointment }) => (
    <tr>
      <td className="table-key">Agent</td>
      <td>
        <div>
          <strong>{appointment.coop.name}</strong>
        </div>
        {appointment.coop.org?.name && <div>{appointment.coop.org.name}</div>}
      </td>
    </tr>
  );

const Status: React.FC<RowProps> = ({ appointment }) => (
    <tr>
      <td className="table-key">Status</td>
      <td
        className="appointment-status"
        data-status={appointment.isPending ? 'pending' : appointment.status}
      >
        {appointment.isPending ? 'Pending' : formatStatus(appointment.status)}
      </td>
    </tr>
  );

const Buyers: React.FC<RowProps> = ({ appointment, $rootScope }) => {
  const [inviteBuyers, setInviteBuyers] = React.useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const [savingBuyer, setSavingBuyer] = React.useState(false);
  const [buyersUpdated, setBuyersUpdated] = React.useState(false);

  React.useEffect(() => {
    const getBuyersLabel = async () => {
      try {
        const buyerContacts = appointment.buyers?.length
          ? await ContactController.findManyClientsByIds(appointment.buyers)
          : [];

        setInviteBuyers(
          buyerContacts
            .map(buyer => ({
              label: `${buyer.firstName} ${buyer.lastName}`,
              value: buyer.id,
            }))
            .sort((buyer1: { label: string }, buyer2: { label: string }) =>
              buyer1.label.localeCompare(buyer2.label),
            ),
        );
      } catch (err) {
        errorReporter.error(`Error loading buyers: ${err}`);
      }
    };

    getBuyersLabel();
  }, []);

  const updateBuyers = async () => {
    setSavingBuyer(true);
    try {
      await AppointmentController.saveBuyers(
        appointment.id,
        inviteBuyers.map(buyer => buyer.value),
      );
    } catch (err) {
      errorReporter.error(`Error saving buyers: ${err}`);
    }

    setBuyersUpdated(false);
    setSavingBuyer(false);
  };

  return (
    <tr>
      <td className="table-key">
        Buyers<StyleTag color="blue1">New</StyleTag>
      </td>
      <td
        className="appointment-status"
        data-status={appointment.isPending ? 'pending' : appointment.status}
      >
        <InviteBuyerContainer>
          <InviteBuyer
            agent={appointment.coop}
            value={inviteBuyers}
            onValueChange={React.useCallback((buyers: any) => {
              setInviteBuyers(buyers);
              setBuyersUpdated(true);
            }, [])}
            listing={appointment.listing}
            $rootScope={$rootScope}
          />
          <Button
            onClick={updateBuyers}
            loading={savingBuyer}
            disabled={!buyersUpdated}
          >
            Update
          </Button>
        </InviteBuyerContainer>
      </td>
    </tr>
  );
};

const OriginalApt: React.FC<RowProps> = ({ appointment }) => {
  const { timezone } = appointment.listing;
  const originalDateStrWithYear = formatDateStrWithYearAndDay(
    appointment.originalTime,
    timezone,
  );
  const originalTimeStr = formatTimeStr(appointment.originalTime, timezone);
  const originalTimeEndStr = formatTimeStr(
    appointment.originalTimeEnd,
    timezone,
  );

  return (
    <tr>
      <td className="table-key">Original Showing</td>
      <td>
        <label>{originalDateStrWithYear}</label>
        <div>{`${originalTimeStr} - ${originalTimeEndStr}`}</div>
      </td>
    </tr>
  );
};

const NewSuggestedApt: React.FC<RowProps> = ({ appointment }) => {
  const tdProps = {
    className: 'appointment-status',
    'data-status': appointment.status,
  };

  const { timezone } = appointment.listing;
  const dateStrWithYear = formatDateStrWithYearAndDay(
    appointment.time,
    timezone,
  );
  const timeStr = formatTimeStr(appointment.time, timezone);
  const timeEndStr = formatTimeStr(appointment.timeEnd, timezone);

  return (
    <tr>
      <td className="table-key" style={{ fontWeight: 400 }} {...tdProps}>
        New Suggested Showing
      </td>
      <td {...tdProps}>
        <label>{dateStrWithYear}</label>
        <div style={{ fontWeight: 400 }}>{`${timeStr} - ${timeEndStr}`}</div>
      </td>
    </tr>
  );
};

export default AppointmentDetailSection;

const StyleTag = styled(Tag)`
  border: 1px solid ${theme.blue6};
  color: ${theme.blue6};
  border-radius: 2px;
  margin-left: 5px;
`;

const InviteBuyerContainer = styled.div`
  display: flex;
  font-weight normal;
  text-transform: none;
`;
